import { useRolodexEntries } from 'features/rolodex'
import {
  ExtraLabelDisplayFn,
  UnitDisplayPropertiesFn,
} from 'features/siteMapViz'
import { useSiteViewContext } from 'features/siteView'
import { isNil } from 'lodash'
import { MapBoxMapViz } from 'pages/siteView/siteMap/mapboxMap/MapBoxMapViz'
import { MaxarTilesetMapViz } from 'pages/siteView/siteMap/tilesetMap/MaxarTilesetMapViz'
import { MapSkeleton } from 'pages/siteView/SiteViewContentSkeleton'
import React, {
  Dispatch,
  FC,
  ReactNode,
  RefObject,
  SetStateAction,
  useEffect,
} from 'react'
import styled from 'styled-components'
import {
  GeckoGeographicPin,
  GeckoSite,
  GeckoSiteModel,
  GeckoUnit,
  RolodexEntryType,
} from 'types'

import { FeatureInteractionContextProvider } from './contexts/FeatureInteractionContext'
import { EditModePanel } from './popupMenus/editMode/EditModePanel'

const Container = styled.div`
  height: 100%;
  width: 100%;
  max-height: 100%;
  display: flex;
  flex-direction: column;
`

const MapContainer = styled.div`
  flex: 1;
  width: 100%;
  min-width: 0;
  min-height: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${({ theme }) => theme.forecast.background4};
`

export type MapInteractionAPIs = {
  /**
   *  A function that will be called on units that have tagged site-model entries, to determine if they should be displayed
   *  A return of undefined means the tagged unit was not found
   */
  shouldDisplayTaggedUnit?: (unitID: string) => boolean | undefined

  /**
   * Function that will return an extra react-node to be displayed in the label
   * for an asset
   *
   * Note: This function needs to be appropriately memoized
   * Note: The React-Node returned cannot use contexts -- React-Three-Fiber
   *        appears to be rendering this outside of our react-tree so it will
   *        fail if you do
   *        TODO [ARCH-330]: Make labels work with contexts
   */
  getExtraLabelDisplay?: ExtraLabelDisplayFn

  /**
   * The ID of a unit that the camera is "flying" to -- once this action is completed, this value will be set to undefined
   *
   * NOTE: in order for unitIDFlyingTo to work, you must also pass in setUnitIDFlyingTo
   */
  unitIDFlyingTo?: string | undefined
  setUnitIDFlyingTo?: (unitID: string | undefined) => void

  /**
   * Function that determines whether a geographic pin should be displayed
   * Returns true if the pin should be displayed, false otherwise
   */
  shouldDisplayPin?: (pin: GeckoGeographicPin) => Promise<boolean>

  /**
   * Whether this map is being used in a walkdown context
   * This affects how geographic pins are displayed and handled
   */
  isWalkdown?: boolean
}

export interface SiteViewMapProps {
  // Site Data Props
  site: GeckoSite
  /**
   * A function that will return the color and pattern for a given unit
   * Note: This function needs to be appropriately memoized
   * Note: Any alpha-value included in the color will be ignored
   */
  getUnitDisplayProperties: UnitDisplayPropertiesFn

  /**
   * The (functional) component to render when a feature is clicked, which will be displayed above the selected feature.
   * This is specified explicitly using the functional paradigm so you can specify other on-click actions like navigate.
   * If unitID is null, that means the user clicked on a feature that is not a unit (e.g. an unlinked site model feature).
   *
   * NOTE: The returned ReactNode will not have a specified parent width/height, so 100% width/height won't work
   */
  componentToDisplayOnFeatureClick:
    | ((unitID: string | null) => ReactNode)
    | undefined

  /** A set of variables which will perform actions on the map */
  mapInteractionAPIs?: MapInteractionAPIs

  /** If provided, the add feature / edit site config buttons will be placed (visually) in the given ref  */
  addMapFeatureMenuLocationRef?: RefObject<HTMLDivElement>
}

export const SiteViewMap: FC<SiteViewMapProps> = (props) => {
  // Get Site Model
  const [siteModel, setSiteModel] = React.useState<GeckoSiteModel | undefined>()

  const [isInEditMode, setIsInEditMode] = React.useState(false)

  const {
    siteModels,
    geographicPins,
    isLoading: isLoadingSiteMapData,
  } = useRolodexEntries({
    type: [RolodexEntryType.SITE_MODEL, RolodexEntryType.GEOGRAPHIC_PIN],
    tags: {
      site: props.site.id,
    },
  })

  const { units: allUnitsAtSite } = useRolodexEntries({
    type: RolodexEntryType.UNIT,
    tags: {
      [RolodexEntryType.SITE]: props.site.id,
    },
  })

  React.useEffect(() => {
    if (isLoadingSiteMapData) return undefined
    const siteModelEntry = siteModels.at(0)
    setSiteModel(siteModelEntry)
  }, [siteModels, isLoadingSiteMapData, setSiteModel])

  const [internalUnitFlyingTo, setInternalUnitFlyingTo] = React.useState<
    string | undefined
  >(undefined)

  const { raycastEnabled } = useSiteViewContext()

  const unitToFlyToToUse = props.mapInteractionAPIs?.setUnitIDFlyingTo
    ? props.mapInteractionAPIs.unitIDFlyingTo
    : internalUnitFlyingTo
  const setUnitIDFlyingToToUse =
    props.mapInteractionAPIs?.setUnitIDFlyingTo ?? setInternalUnitFlyingTo

  const [filteredPins, setFilteredPins] = React.useState<
    ReadonlyArray<GeckoGeographicPin>
  >([])
  useEffect(() => {
    const shouldDisplayPin = props.mapInteractionAPIs?.shouldDisplayPin
    if (isNil(shouldDisplayPin)) {
      setFilteredPins(geographicPins)
      return
    }

    Promise.all(
      geographicPins.map(async (pin) => {
        const shouldShow = await shouldDisplayPin(pin)
        if (shouldShow) {
          return pin
        }
        return null
      })
    ).then((shouldShowArr) => {
      const fPin: Array<GeckoGeographicPin> = shouldShowArr.filter(
        (pin) => !isNil(pin)
      )
      setFilteredPins(fPin)
    })
  }, [geographicPins, setFilteredPins, props.mapInteractionAPIs])

  return (
    <Container>
      <EditModePanel
        isInEditMode={isInEditMode}
        exitEditMode={() => {
          setIsInEditMode(false)
        }}
      />
      <MapContainer>
        <FeatureInteractionContextProvider raycastEnabled={raycastEnabled}>
          {isLoadingSiteMapData ? (
            <MapSkeleton />
          ) : (
            <MapToDisplay
              site={props.site}
              allUnitsAtSite={allUnitsAtSite}
              siteModel={siteModel}
              setSiteModel={setSiteModel}
              getUnitDisplayProperties={props.getUnitDisplayProperties}
              isInEditMode={isInEditMode}
              setIsInEditMode={setIsInEditMode}
              // Map Interaction APIs
              shouldDisplayTaggedUnit={
                props.mapInteractionAPIs?.shouldDisplayTaggedUnit ??
                (() => true)
              }
              getExtraLabelDisplay={
                props.mapInteractionAPIs?.getExtraLabelDisplay ?? (() => null)
              }
              unitIDFlyingTo={unitToFlyToToUse}
              setUnitIDFlyingTo={setUnitIDFlyingToToUse}
              componentToDisplayOnFeatureClick={
                props.componentToDisplayOnFeatureClick
              }
              // Display Augmenters
              addMapFeatureMenuLocationRef={props.addMapFeatureMenuLocationRef}
              pins={filteredPins}
            />
          )}
        </FeatureInteractionContextProvider>
      </MapContainer>
    </Container>
  )
}

/**
 * Display the map based on the map type
 *
 * In the future I think this should be done using a headless UI, but for now this will do
 */
const MapToDisplay: FC<{
  site: GeckoSite
  allUnitsAtSite: ReadonlyArray<GeckoUnit>
  siteModel: GeckoSiteModel | undefined
  setSiteModel: (siteModel: GeckoSiteModel) => void
  shouldDisplayTaggedUnit: NonNullable<
    MapInteractionAPIs['shouldDisplayTaggedUnit']
  >
  getUnitDisplayProperties: UnitDisplayPropertiesFn
  getExtraLabelDisplay: ExtraLabelDisplayFn
  unitIDFlyingTo: string | undefined
  setUnitIDFlyingTo: (unitID: string | undefined) => void
  addMapFeatureMenuLocationRef: RefObject<HTMLDivElement> | undefined
  isInEditMode: boolean
  setIsInEditMode: Dispatch<SetStateAction<boolean>>
  pins: readonly GeckoGeographicPin[]
  componentToDisplayOnFeatureClick: SiteViewMapProps['componentToDisplayOnFeatureClick']
}> = (props) => {
  if (!isNil(props.siteModel)) {
    return (
      <MaxarTilesetMapViz
        siteModel={props.siteModel}
        setSiteModel={props.setSiteModel}
        allUnitsAtSite={props.allUnitsAtSite}
        getUnitDisplayProperties={props.getUnitDisplayProperties}
        getExtraLabelDisplay={props.getExtraLabelDisplay}
        addMapFeatureMenuLocationRef={props.addMapFeatureMenuLocationRef}
        shouldDisplayTaggedUnit={props.shouldDisplayTaggedUnit}
        unitIDFlyingTo={props.unitIDFlyingTo}
        setUnitIDFlyingTo={props.setUnitIDFlyingTo}
        isInEditMode={props.isInEditMode}
        setIsInEditMode={props.setIsInEditMode}
        pins={props.pins}
        componentToDisplayOnFeatureClick={
          props.componentToDisplayOnFeatureClick
        }
      />
    )
  }
  return (
    <MapBoxMapViz
      site={props.site}
      allUnitsAtSite={props.allUnitsAtSite}
      getUnitDisplayProperties={props.getUnitDisplayProperties}
      shouldDisplayTaggedUnit={props.shouldDisplayTaggedUnit}
      unitIDFlyingTo={props.unitIDFlyingTo}
      setUnitIDFlyingTo={props.setUnitIDFlyingTo}
      getExtraLabelDisplay={props.getExtraLabelDisplay}
      componentToDisplayOnFeatureClick={props.componentToDisplayOnFeatureClick}
      pins={props.pins}
    />
  )
}
